/**
 * Supplier Statement (FSD 2.2.2.6 `GET …/suppliers/{id}/statement`, 2.2.2.10; FT10-U16).
 *
 * A thin wrapper over the generated client's `apReportsVendorStatement`. Only the
 * parameters that carry meaning travel: an empty date is an absent key, and
 * `openOnly` is sent only when it narrows the statement (the server's default
 * lists open AND settled documents, which is what the FSD's statement is).
 */

import { apReportsVendorStatement, type ApLedgerQuery, type VendorStatement } from '@erpx/api'

import { api } from '@/api/client'

export type { VendorStatement }

/** The route's own guard (`ap.report.read`), and the lookups the filters need. */
export const SUPPLIER_STATEMENT_PERMISSIONS = {
  reportRead: 'ap.report.read',
  vendorRead: 'ap.vendor.read',
  companyRead: 'org.company.read',
} as const

export interface SupplierStatementFilters {
  companyId: number
  vendorId: number
  from: string
  to: string
  openOnly: boolean
}

export const SUPPLIER_STATEMENT_KEYS = {
  all: ['ap', 'reports', 'supplier-statement'] as const,
  one: (filters: SupplierStatementFilters) =>
    ['ap', 'reports', 'supplier-statement', filters] as const,
} as const

export function statementQuery(filters: SupplierStatementFilters): ApLedgerQuery {
  return {
    companyId: filters.companyId,
    ...(filters.from === '' ? {} : { from: filters.from }),
    ...(filters.to === '' ? {} : { to: filters.to }),
    ...(filters.openOnly ? { openOnly: true } : {}),
  }
}

export function supplierStatement(filters: SupplierStatementFilters): Promise<VendorStatement> {
  return apReportsVendorStatement(api(), { id: String(filters.vendorId) }, statementQuery(filters))
}
